import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import { roundToNumDecimals } from '../Common/Units.js'
import * as psy from './Psychrometrics.js'

import {
  Field, FieldType,
  ProjectUnits,
} from '../Common/Field.js'
import { CalcContext } from '../Common/CalcContext.js'
import { ObjectVisitor } from '../Common/ObjectVisitor.js'

import { InputComponent } from '../Common/InputComponent.js'
import { Updater } from '../Common/Updater.js'
import { Watcher } from '../Common/Watcher.js'
import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { prettyJson } from '../SharedUtils.js'
import { LocalStorageSaver } from '../Common/LocalStorageSaver.js'


/*
From Psychrometrics.js:
export let PsyCalcMethod = {
  CalcWithWetBulbTemp: 'CalcWithWetBulbTemp',
  CalcWithRelativeHumidity: 'CalcWithRelativeHumidity',
  CalcWithHumidityRatio: 'CalcWithHumidityRatio',
  CalcWithDewpointTemp: 'CalcWithDewpointTemp',
  CalcWithSpecificVolume: 'CalcWithSpecificVolume',
  CalcWithEnthalpy: 'CalcWithEnthalpy',
};
*/

let CalculationMethod = makeEnum({
  CalcWithWetBulbTemp: 'Wet bulb temp.',
  CalcWithRelativeHumidity: 'Relative humidity',
  CalcWithHumidityRatio: 'Humidity ratio',
  CalcWithDewpointTemp: 'Dewpoint temp.',
  // Note - do not support this for now. Not very useful.
  //CalcWithSpecificVolume: 'CalcWithSpecificVolume',
  CalcWithEnthalpy: 'Enthalpy',
})

let ProcessType = makeEnum({
  None: 'None',
  Heating: 'Heating',
  Cooling: 'Cooling',
  Humidification: 'Humidification',
  Mixing: 'Mixing',
})
export { ProcessType }

let HeatingMethod = makeEnum({
  ByTemperature: 'By new temperature',
  ByHeatAdded: 'By heat added (MBH)',
})

let CoolingMethod = makeEnum({
  ByTemperature: 'By new temperature',
  ByState: 'By new psychrometric state',
  ByPower: 'By cooling power (MBH)',
})

let HumidificationMethod = makeEnum({
  ByState: 'By new psychrometric state',
  ByWaterFlow: 'By water added (lb/hr)',
})

export class MixingAirstream extends InputComponent {
  init(watcher, calculator) {
    this.updater.setWatcher(watcher);
    this.calculator = calculator;

    this.dryBulbTemp = new Field({
      name: 'Dry Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 55,
      projectContext: calculator,
    })
    this.airflow = new Field({
      name: 'Airflow',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      defaultValue: 500,
      projectContext: calculator,
    })
    this.calculationMethod = Field.makeSelect('Second input', CalculationMethod)

    this.wetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 50,
      projectContext: calculator,
    })
    this.wetBulbTemp.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithWetBulbTemp)
    this.relativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.relativeHumidity.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithRelativeHumidity)
    this.humidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.humidityRatio.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithHumidityRatio)
    this.dewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.dewpointTemp.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithDewpointTemp)
    this.specificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.specificVolume.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithSpecificVolume)
    this.enthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.enthalpy.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithEnthalpy)
    this.inputFields = [
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]

    this.outputWetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputRelativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputHumidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputDewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputSpecificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputEnthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputFields = [
      'outputWetBulbTemp',
      'outputRelativeHumidity',
      'outputHumidityRatio',
      'outputDewpointTemp',
      'outputSpecificVolume',
      'outputEnthalpy',
    ]

    this.updater.addWatchEffect('update-output', () => {
      let res = this.calcOutput();
      this.outputWetBulbTemp.value = res.t_wb_F;
      this.outputRelativeHumidity.value = res.RH * 100;
      this.outputHumidityRatio.value = res.W;
      this.outputDewpointTemp.value = res.t_dp_F;
      this.outputSpecificVolume.value = res.v;
      this.outputEnthalpy.value = res.h;
    })

    this.serFields = [
      'dryBulbTemp',
      'airflow',
      'calculationMethod',
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'MixingAirstream',
      path: 'mixing-airstream',
    }
  }

  calcOutput() {
    return this._calcOutputUsingMethod(this.calculationMethod.value);
  }

  _calcOutputUsingMethod(calculationMethod) {
    let ctx = CalcContext.create();
    let inputValue = {};
    switch (calculationMethod) {
      case CalculationMethod.CalcWithWetBulbTemp:
        inputValue = { t_wb_F: this.wetBulbTemp.value };
        break;
      case CalculationMethod.CalcWithRelativeHumidity:
        inputValue = { RH: this.relativeHumidity.value / 100 };
        break;
      case CalculationMethod.CalcWithHumidityRatio:
        inputValue = { W: this.humidityRatio.value };
        break;
      case CalculationMethod.CalcWithDewpointTemp:
        inputValue = { t_dp_F: this.dewpointTemp.value };
        break;
      case CalculationMethod.CalcWithSpecificVolume:
        inputValue = { v: this.specificVolume.value };
        break;
      case CalculationMethod.CalcWithEnthalpy:
        inputValue = { h: this.enthalpy.value };
        break;
      default:
        throw new Error(`Unknown calculation method: ${calculationMethod}`);
    }
    let res = null;
    try {
      res = ctx.call(psy.CalcPsychrometrics, this.dryBulbTemp.value, this.calculator.altitude.value,
        calculationMethod, inputValue);
    } catch (e) {
      console.log(e);
      ctx.logFatalError(e);
      throw e;
    }
    return res;
  }
}
setupClass(MixingAirstream)

// CoolingTargetState: defines a target psychrometric state for the cooling process
// (same as MixingAirstream but without an airflow field)
export class CoolingTargetState extends InputComponent {
  init(watcher, calculator) {
    this.updater.setWatcher(watcher);
    this.calculator = calculator;

    this.dryBulbTemp = new Field({
      name: 'Dry Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 55,
      projectContext: calculator,
    })
    this.calculationMethod = Field.makeSelect('Second input', CalculationMethod)

    this.wetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 50,
      projectContext: calculator,
    })
    this.wetBulbTemp.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithWetBulbTemp)
    this.relativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.relativeHumidity.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithRelativeHumidity)
    this.humidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.humidityRatio.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithHumidityRatio)
    this.dewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.dewpointTemp.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithDewpointTemp)
    this.specificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.specificVolume.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithSpecificVolume)
    this.enthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.enthalpy.setVisibility(() =>
      this.calculationMethod.value == CalculationMethod.CalcWithEnthalpy)
    this.inputFields = [
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]

    this.outputWetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputRelativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputHumidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputDewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputSpecificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputEnthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputFields = [
      'outputWetBulbTemp',
      'outputRelativeHumidity',
      'outputHumidityRatio',
      'outputDewpointTemp',
      'outputSpecificVolume',
      'outputEnthalpy',
    ]

    this.updater.addWatchEffect('update-output', () => {
      let res = this.calcOutput();
      this.outputWetBulbTemp.value = res.t_wb_F;
      this.outputRelativeHumidity.value = res.RH * 100;
      this.outputHumidityRatio.value = res.W;
      this.outputDewpointTemp.value = res.t_dp_F;
      this.outputSpecificVolume.value = res.v;
      this.outputEnthalpy.value = res.h;
    })

    this.serFields = [
      'dryBulbTemp',
      'calculationMethod',
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'CoolingTargetState',
      path: 'cooling-target-state',
    }
  }

  calcOutput() {
    return this._calcOutputUsingMethod(this.calculationMethod.value);
  }

  _calcOutputUsingMethod(calculationMethod) {
    let ctx = CalcContext.create();
    let inputValue = {};
    switch (calculationMethod) {
      case CalculationMethod.CalcWithWetBulbTemp:
        inputValue = { t_wb_F: this.wetBulbTemp.value };
        break;
      case CalculationMethod.CalcWithRelativeHumidity:
        inputValue = { RH: this.relativeHumidity.value / 100 };
        break;
      case CalculationMethod.CalcWithHumidityRatio:
        inputValue = { W: this.humidityRatio.value };
        break;
      case CalculationMethod.CalcWithDewpointTemp:
        inputValue = { t_dp_F: this.dewpointTemp.value };
        break;
      case CalculationMethod.CalcWithSpecificVolume:
        inputValue = { v: this.specificVolume.value };
        break;
      case CalculationMethod.CalcWithEnthalpy:
        inputValue = { h: this.enthalpy.value };
        break;
      default:
        throw new Error(`Unknown calculation method: ${calculationMethod}`);
    }
    let res = null;
    try {
      res = ctx.call(psy.CalcPsychrometrics, this.dryBulbTemp.value, this.calculator.altitude.value,
        calculationMethod, inputValue);
    } catch (e) {
      console.log(e);
      ctx.logFatalError(e);
      throw e;
    }
    return res;
  }

  static create(watcher, calculator) {
    let obj = new CoolingTargetState();
    obj.init(watcher, calculator);
    setupClass(CoolingTargetState);
    return obj;
  }
}
setupClass(CoolingTargetState)

export class PsychrometricsPoint extends InputComponent {
  init(watcher, calculator) {
    this.updater.setWatcher(watcher);
    this.calculator = calculator;

    // Stable identifier for this point, used by point-selector dropdowns
    this.stableId = Math.random().toString(36).substring(2, 15);

    this.dryBulbTemp = new Field({
      name: 'Dry Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 75,
      projectContext: calculator,
    })
    this.airflow = new Field({
      name: 'Airflow',
      type: FieldType.AirFlow,
      watcher: this.watcher,
      defaultValue: 1000,
      projectContext: calculator,
    })
    this.calculationMethod = Field.makeSelect('Second input', CalculationMethod)

    this.wetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      defaultValue: 63,
      projectContext: calculator,
    })
    this.wetBulbTemp.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithWetBulbTemp;
    })
    this.relativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.relativeHumidity.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithRelativeHumidity;
    })
    this.humidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.humidityRatio.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithHumidityRatio;
    });
    this.dewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.dewpointTemp.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithDewpointTemp;
    });
    this.specificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.specificVolume.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithSpecificVolume;
    })
    this.enthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      watcher: this.watcher,
      projectContext: calculator,
    })
    this.enthalpy.setVisibility(() => {
      return this.calculationMethod.value == CalculationMethod.CalcWithEnthalpy;
    });
    this.inputFields = [
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]

    this.outputWetBulbTemp = new Field({
      name: 'Wet Bulb Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputRelativeHumidity = new Field({
      name: 'Relative Humidity',
      type: FieldType.Percent,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputHumidityRatio = new Field({
      name: 'Humidity Ratio',
      type: FieldType.HumidityRatio,
      numDecimals: 4,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputDewpointTemp = new Field({
      name: 'Dewpoint Temperature',
      type: FieldType.Temperature,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputSpecificVolume = new Field({
      name: 'Specific Volume',
      type: FieldType.SpecificVolume,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputEnthalpy = new Field({
      name: 'Enthalpy',
      type: FieldType.Enthalpy,
      isOutput: true,
      projectContext: calculator,
    })
    this.outputFields = [
      'outputWetBulbTemp',
      'outputRelativeHumidity',
      'outputHumidityRatio',
      'outputDewpointTemp',
      'outputSpecificVolume',
      'outputEnthalpy',
    ]

    this.randName = Math.random().toString(36).substring(2, 15);
    this.prevCalculationMethod = this.calculationMethod.value;
    this.updater.addWatchEffect('calc-method-change', () => {
      // When calculation method changes, copy the corresponding output value to the new input,
      // so that the results don't suddenly jump around.
      let newMethod = this.calculationMethod.value;
      if (newMethod === this.prevCalculationMethod) {
        // Only act if the method actually changed
        return;
      }
      console.log(`Calculation method changed from ${this.prevCalculationMethod} to ${newMethod} for ${this.randName}`);
      if (newMethod == CalculationMethod.CalcWithWetBulbTemp) {
        if (this.outputRelativeHumidity.value >= 100) {
          // Some special handling here to keep the UI acting "normal" when switching to Wet-bulb mode
          // from a state where RH>100. When RH>100, the wet bulb calculation fails, so we first calculate
          // from RH=100 to get a valid wet bulb temp.
          let ctx = CalcContext.create();
          let res = ctx.call(psy.CalcPsychrometrics, this.dryBulbTemp.value,
            this.calculator.altitude.value, CalculationMethod.CalcWithRelativeHumidity, {
              RH: 1.0,
          });
          console.log(`Correcting wet bulb temp input to ${res.t_wb_F} due to RH>100%`);
          this.wetBulbTemp.value = roundToNumDecimals(res.t_wb_F, 4);
        } else {
          this.wetBulbTemp.value = roundToNumDecimals(this.outputWetBulbTemp.value, 4);
        }
        this.wetBulbTemp.clampToMinMax();
      } else if (newMethod == CalculationMethod.CalcWithRelativeHumidity) {
        this.relativeHumidity.value = roundToNumDecimals(this.outputRelativeHumidity.value, 4);
        this.relativeHumidity.clampToMinMax();
      } else if (newMethod == CalculationMethod.CalcWithHumidityRatio) {
        this.humidityRatio.value = roundToNumDecimals(this.outputHumidityRatio.value, 6);
        this.humidityRatio.clampToMinMax();
      } else if (newMethod == CalculationMethod.CalcWithDewpointTemp) {
        this.dewpointTemp.value = roundToNumDecimals(this.outputDewpointTemp.value, 4);
        this.dewpointTemp.clampToMinMax();
      } else if (newMethod == CalculationMethod.CalcWithSpecificVolume) {
        this.specificVolume.value = roundToNumDecimals(this.outputSpecificVolume.value, 4);
        this.specificVolume.clampToMinMax();
      } else if (newMethod == CalculationMethod.CalcWithEnthalpy) {
        this.enthalpy.value = roundToNumDecimals(this.outputEnthalpy.value, 4);
        this.enthalpy.clampToMinMax();
      } else {
        throw new Error(`Unknown calculation method: ${newMethod}`);
      }
      this.prevCalculationMethod = newMethod;
    })
    this.updater.addWatchEffect('update-output', () => {
      let res = this.calcOutput();
      this.outputWetBulbTemp.value = res.t_wb_F;
      this.outputRelativeHumidity.value = res.RH * 100;
      this.outputHumidityRatio.value = res.W;
      this.outputDewpointTemp.value = res.t_dp_F;
      this.outputSpecificVolume.value = res.v;
      this.outputEnthalpy.value = res.h;

      // Update the other input fields, too, to keep them in sync in case
      // we change the calculation method later
    })

    this.serFields = [
      'stableId',
      'dryBulbTemp',
      'airflow',
      'calculationMethod',
      'wetBulbTemp',
      'relativeHumidity',
      'humidityRatio',
      'dewpointTemp',
      'specificVolume',
      'enthalpy',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'PsychroPoint',
      path: 'psychro-point',
    }
  }

  calcOutput() {
    return this._calcOutputUsingMethod(this.calculationMethod.value);
  }

  _calcOutputUsingMethod(calculationMethod) {
    let ctx = CalcContext.create();

    // Note - do not set all input values here because it messes up the reactivity in
    // the watch effects. Should only depend on the corresponding input field here.
    let inputValue = {};
    switch (calculationMethod) {
      case CalculationMethod.CalcWithWetBulbTemp:
        inputValue = { t_wb_F: this.wetBulbTemp.value };
        break;
      case CalculationMethod.CalcWithRelativeHumidity:
        inputValue = { RH: this.relativeHumidity.value / 100 };
        break;
      case CalculationMethod.CalcWithHumidityRatio:
        inputValue = { W: this.humidityRatio.value };
        break;
      case CalculationMethod.CalcWithDewpointTemp:
        inputValue = { t_dp_F: this.dewpointTemp.value };
        break;
      case CalculationMethod.CalcWithSpecificVolume:
        inputValue = { v: this.specificVolume.value };
        break;
      case CalculationMethod.CalcWithEnthalpy:
        inputValue = { h: this.enthalpy.value };
        break;
      default:
        throw new Error(`Unknown calculation method: ${calculationMethod}`);
    }

    let res = null;
    try {
      res = ctx.call(psy.CalcPsychrometrics, this.dryBulbTemp.value, this.calculator.altitude.value,
        calculationMethod, inputValue);
    } catch (e) {
      console.log(e);
      ctx.logFatalError(e);
      throw e;
    }
    return res;
  }

  getAsChartPoint() {
    let res = this.calcOutput();
    return new psy.ChartPoint({
      name: 'Point 1',
      dryBulbTemp: this.dryBulbTemp.value,
      wetBulbTemp: res.t_wb_F,
      relativeHumidity: res.RH * 100,
      humidityRatio: res.W,
      dewpointTemp: res.t_dp_F,
      specificVolume: res.v,
      enthalpy: res.h,
    });
  }

}
setupClass(PsychrometricsPoint)

export class PsychroProcess extends InputComponent {
  init(watcher, calculator) {
    this.updater.setWatcher(watcher);
    this.calculator = calculator;

    this.processType = Field.makeSelect('Process Type', ProcessType)

    // --- Heating process fields ---
    this.heatingMethod = Field.makeSelect('Heating method', HeatingMethod)
    this.heatingMethod.setVisibility(() => this.processType.value === ProcessType.Heating)
    this.heatingTargetTemp = new Field({
      name: 'Target Temperature', type: FieldType.Temperature,
      watcher: this.watcher, defaultValue: 80, projectContext: calculator,
    })
    this.heatingTargetTemp.setVisibility(() =>
      this.processType.value === ProcessType.Heating &&
      this.heatingMethod.value === HeatingMethod.ByTemperature)
    this.heatingTotalHeat = new Field({
      name: 'Total Heat Added', type: FieldType.LoadMBH,
      watcher: this.watcher, defaultValue: 10, min: 0, projectContext: calculator,
    })
    this.heatingTotalHeat.setVisibility(() =>
      this.processType.value === ProcessType.Heating &&
      this.heatingMethod.value === HeatingMethod.ByHeatAdded)

    // --- Cooling process fields ---
    this.coolingMethod = Field.makeSelect('Cooling method', CoolingMethod)
    this.coolingMethod.setVisibility(() => this.processType.value === ProcessType.Cooling)
    this.coolingTargetTemp = new Field({
      name: 'Target Temperature', type: FieldType.Temperature,
      watcher: this.watcher, defaultValue: 55, projectContext: calculator,
    })
    this.coolingTargetTemp.setVisibility(() =>
      this.processType.value === ProcessType.Cooling &&
      this.coolingMethod.value === CoolingMethod.ByTemperature)
    this.coolingPower = new Field({
      name: 'Cooling Power', type: FieldType.LoadMBH,
      watcher: this.watcher, defaultValue: 10, min: 0, projectContext: calculator,
    })
    this.coolingPower.setVisibility(() =>
      this.processType.value === ProcessType.Cooling &&
      this.coolingMethod.value === CoolingMethod.ByPower)

    // --- Humidification process fields ---
    this.humidificationMethod = Field.makeSelect('Humidification method', HumidificationMethod)
    this.humidificationMethod.setVisibility(() => this.processType.value === ProcessType.Humidification)
    this.waterFlow = new Field({
      name: 'Water Flow', type: FieldType.WaterFlowRate,
      watcher: this.watcher, defaultValue: 10, min: 0, projectContext: calculator,
    })
    this.waterFlow.setVisibility(() =>
      this.processType.value === ProcessType.Humidification &&
      this.humidificationMethod.value === HumidificationMethod.ByWaterFlow)
    this.waterTemp = new Field({
      name: 'Steam Temperature', type: FieldType.Temperature,
      watcher: this.watcher, defaultValue: 140, projectContext: calculator,
    })
    this.waterTemp.setVisibility(() =>
      this.processType.value === ProcessType.Humidification &&
      this.humidificationMethod.value === HumidificationMethod.ByWaterFlow)

    this.processFields = [
      'heatingMethod', 'heatingTargetTemp', 'heatingTotalHeat',
      'coolingMethod', 'coolingTargetTemp', 'coolingPower',
      'humidificationMethod', 'waterFlow', 'waterTemp',
    ]

    this.mixingAirstream = MixingAirstream.create(watcher, calculator);
    this.coolingTargetState = CoolingTargetState.create(watcher, calculator);
    this.humidificationTargetState = CoolingTargetState.create(watcher, calculator);

    // --- Result state output fields ---
    this.resultDryBulbTemp = new Field({ name: 'Dry Bulb Temperature', type: FieldType.Temperature, isOutput: true, projectContext: calculator })
    this.resultWetBulbTemp = new Field({ name: 'Wet Bulb Temperature', type: FieldType.Temperature, isOutput: true, projectContext: calculator })
    this.resultRelativeHumidity = new Field({ name: 'Relative Humidity', type: FieldType.Percent, isOutput: true, projectContext: calculator })
    this.resultHumidityRatio = new Field({ name: 'Humidity Ratio', type: FieldType.HumidityRatio, numDecimals: 4, isOutput: true, projectContext: calculator })
    this.resultDewpointTemp = new Field({ name: 'Dewpoint Temperature', type: FieldType.Temperature, isOutput: true, projectContext: calculator })
    this.resultSpecificVolume = new Field({ name: 'Specific Volume', type: FieldType.SpecificVolume, isOutput: true, projectContext: calculator })
    this.resultEnthalpy = new Field({ name: 'Enthalpy', type: FieldType.Enthalpy, isOutput: true, projectContext: calculator })
    // Active airflow after this process (unchanged unless Mixing)
    this.resultActiveAirflow = new Field({ name: 'Airflow Out', type: FieldType.AirFlow, isOutput: true, projectContext: calculator })
    this.resultStateFields = [
      'resultDryBulbTemp', 'resultWetBulbTemp', 'resultRelativeHumidity',
      'resultHumidityRatio', 'resultDewpointTemp', 'resultSpecificVolume', 'resultEnthalpy',
    ]

    // --- Process result output fields ---
    this.resultSensibleHeat = new Field({ name: 'Sensible Heat', type: FieldType.LoadMBH, isOutput: true, projectContext: calculator })
    this.resultAirflow = new Field({ name: 'Resulting Airflow', type: FieldType.AirFlow, isOutput: true, projectContext: calculator })
    this.resultSHR = new Field({ name: 'Sensible Heat Ratio', type: FieldType.Ratio, isOutput: true, projectContext: calculator })
    this.resultTotalCooling = new Field({ name: 'Total Cooling', type: FieldType.LoadMBH, isOutput: true, projectContext: calculator })
    this.resultLatentCooling = new Field({ name: 'Latent Cooling', type: FieldType.LoadMBH, isOutput: true, projectContext: calculator })
    this.resultLatentHeat = new Field({ name: 'Latent Heat Added', type: FieldType.LoadMBH, isOutput: true, projectContext: calculator })
    this.resultSensibleHeat.setVisibility(() =>
      this.processType.value === ProcessType.Heating ||
      this.processType.value === ProcessType.Humidification)
    this.resultAirflow.setVisibility(() => this.processType.value === ProcessType.Mixing)
    this.resultSHR.setVisibility(() =>
      this.processType.value === ProcessType.Heating ||
      this.processType.value === ProcessType.Cooling)
    this.resultTotalCooling.setVisibility(() => this.processType.value === ProcessType.Cooling)
    this.resultLatentCooling.setVisibility(() => this.processType.value === ProcessType.Cooling)
    this.resultLatentHeat.setVisibility(() => this.processType.value === ProcessType.Humidification)
    this.processResultFields = [
      'resultSensibleHeat', 'resultTotalCooling', 'resultLatentCooling',
      'resultLatentHeat', 'resultAirflow', 'resultSHR',
    ]

    this.updater.addWatchEffect('update-result', () => {
      const idx = this.calculator.processes.indexOf(this);
      let incomingState, incomingAirflow, T_incoming;
      if (idx <= 0) {
        const sp = this.calculator.getMainPoint();
        try {
          incomingState = sp.calcOutput();
          incomingAirflow = sp.airflow.value;
          T_incoming = sp.dryBulbTemp.value;
        } catch(e) { return; }
      } else {
        const prev = this.calculator.processes[idx - 1];
        // Reading prev's output fields creates a reactive dependency chain
        incomingState = {
          W: prev.resultHumidityRatio.value,
          h: prev.resultEnthalpy.value,
          t_wb_F: prev.resultWetBulbTemp.value,
          RH: prev.resultRelativeHumidity.value / 100,
          t_dp_F: prev.resultDewpointTemp.value,
          v: prev.resultSpecificVolume.value,
        };
        incomingAirflow = prev.resultActiveAirflow.value;
        T_incoming = prev.resultDryBulbTemp.value;
      }
      const altitude = this.calculator.altitude.value;
      const result = this.calcResult(incomingState, incomingAirflow, T_incoming, altitude);
      if (result) {
        const { state2, T_db2, Q_sensible_MBH, SHR, resultAirflow,
                Q_total_cooling, Q_latent_cooling, Q_latent_heat } = result;
        this.resultDryBulbTemp.value = T_db2;
        this.resultWetBulbTemp.value = state2.t_wb_F;
        this.resultRelativeHumidity.value = state2.RH * 100;
        this.resultHumidityRatio.value = state2.W;
        this.resultDewpointTemp.value = state2.t_dp_F;
        this.resultSpecificVolume.value = state2.v;
        this.resultEnthalpy.value = state2.h;
        this.resultActiveAirflow.value = resultAirflow ?? incomingAirflow;
        if (Q_sensible_MBH !== null) this.resultSensibleHeat.value = Q_sensible_MBH;
        if (resultAirflow !== null) this.resultAirflow.value = resultAirflow;
        if (SHR !== null) this.resultSHR.value = SHR;
        if (Q_total_cooling !== null) this.resultTotalCooling.value = Q_total_cooling;
        if (Q_latent_cooling !== null) this.resultLatentCooling.value = Q_latent_cooling;
        if (Q_latent_heat !== null) this.resultLatentHeat.value = Q_latent_heat;
      }
    })

    this.serFields = [
      'processType',
      'heatingMethod', 'heatingTargetTemp', 'heatingTotalHeat',
      'coolingMethod', 'coolingTargetTemp', 'coolingPower',
      'humidificationMethod', 'waterFlow', 'waterTemp',
      'mixingAirstream', 'coolingTargetState', 'humidificationTargetState',
    ]
    this.childObjs = '$auto'
    this.objInfo = { _name: 'PsychroProcess', path: 'psychro-process' }
  }

  calcResult(incomingState, incomingAirflow, T_incoming, altitude) {
    if (this.processType.value === ProcessType.None) {
      return { state2: incomingState, T_db2: T_incoming, Q_sensible_MBH: null, SHR: null,
               resultAirflow: incomingAirflow, Q_total_cooling: null, Q_latent_cooling: null, Q_latent_heat: null };
    }
    const T1 = T_incoming;
    const W1 = incomingState.W;
    const h1 = incomingState.h;
    const v1 = incomingState.v;
    const cfm = incomingAirflow;
    const massFlowDryAir = (cfm * 60) / v1;

    if (this.processType.value === ProcessType.Heating) {
      const W2 = W1;
      let T_db2, h2, Q_sensible_MBH;
      if (this.heatingMethod.value === HeatingMethod.ByTemperature) {
        T_db2 = this.heatingTargetTemp.value;
        h2 = T_db2 * (0.240 + W2 * 0.444) + W2 * 1061;
        Q_sensible_MBH = massFlowDryAir * (h2 - h1) / 1000;
      } else {
        Q_sensible_MBH = this.heatingTotalHeat.value;
        h2 = h1 + (Q_sensible_MBH * 1000) / massFlowDryAir;
        T_db2 = (h2 - W2 * 1061) / (0.240 + W2 * 0.444);
      }
      const ctx = CalcContext.create();
      let state2;
      try {
        state2 = ctx.call(psy.CalcPsychrometrics, T_db2, altitude,
          CalculationMethod.CalcWithHumidityRatio, { W: W2 });
      } catch(e) { return null; }
      return { state2, T_db2, Q_sensible_MBH, SHR: 1.0, resultAirflow: null,
               Q_total_cooling: null, Q_latent_cooling: null, Q_latent_heat: null };
    }

    if (this.processType.value === ProcessType.Mixing) {
      let state2;
      try { state2 = this.mixingAirstream.calcOutput(); } catch(e) { return null; }
      const W2 = state2.W;
      const h2 = state2.h;
      const v2 = state2.v;
      const cfm2 = this.mixingAirstream.airflow.value;
      const massFlowDryAir2 = (cfm2 * 60) / v2;
      const W_mix = (massFlowDryAir * W1 + massFlowDryAir2 * W2) / (massFlowDryAir + massFlowDryAir2);
      const h_mix = (massFlowDryAir * h1 + massFlowDryAir2 * h2) / (massFlowDryAir + massFlowDryAir2);
      const T_db_mix = (h_mix - W_mix * 1061) / (0.240 + W_mix * 0.444);
      const ctx = CalcContext.create();
      let stateMix;
      try {
        stateMix = ctx.call(psy.CalcPsychrometrics, T_db_mix, altitude,
          CalculationMethod.CalcWithHumidityRatio, { W: W_mix });
      } catch(e) { return null; }
      return { state2: stateMix, T_db2: T_db_mix, Q_sensible_MBH: null, SHR: null,
               resultAirflow: cfm + cfm2, Q_total_cooling: null, Q_latent_cooling: null, Q_latent_heat: null };
    }

    if (this.processType.value === ProcessType.Humidification) {
      let state2, T_db2;
      if (this.humidificationMethod.value === HumidificationMethod.ByState) {
        try { state2 = this.humidificationTargetState.calcOutput(); } catch(e) { return null; }
        T_db2 = this.humidificationTargetState.dryBulbTemp.value;
      } else if (this.humidificationMethod.value === HumidificationMethod.ByWaterFlow) {
        const mw = this.waterFlow.value;
        const T_w = this.waterTemp.value;
        const h_water = 1061 + 0.444 * T_w;
        const deltaW = mw / massFlowDryAir;
        const W2_local = W1 + deltaW;
        const h2_local = h1 + deltaW * h_water;
        T_db2 = (h2_local - W2_local * 1061) / (0.240 + W2_local * 0.444);
        const ctx = CalcContext.create();
        try {
          state2 = ctx.call(psy.CalcPsychrometrics, T_db2, altitude,
            CalculationMethod.CalcWithHumidityRatio, { W: W2_local });
        } catch(e) { return null; }
      } else { return null; }
      const T2 = T_db2;
      const W2 = state2.W;
      const Q_sensible_MBH = massFlowDryAir * (T2 - T1) * (0.240 + W1 * 0.444) / 1000;
      const Q_latent_heat = massFlowDryAir * (W2 - W1) * (1061 + 0.444 * T2) / 1000;
      return { state2, T_db2: T2, Q_sensible_MBH, SHR: null, resultAirflow: null,
               Q_total_cooling: null, Q_latent_cooling: null, Q_latent_heat };
    }

    if (this.processType.value === ProcessType.Cooling) {
      const calcSatState = (T) => {
        const ctx = CalcContext.create();
        return ctx.call(psy.CalcPsychrometrics, T, altitude,
          CalculationMethod.CalcWithRelativeHumidity, { RH: 1.0 });
      };
      let state2, T_db2;
      if (this.coolingMethod.value === CoolingMethod.ByTemperature) {
        T_db2 = this.coolingTargetTemp.value;
        let satAtTarget;
        try { satAtTarget = calcSatState(T_db2); } catch(e) { return null; }
        if (W1 > satAtTarget.W) {
          state2 = satAtTarget;
        } else {
          const ctx = CalcContext.create();
          state2 = ctx.call(psy.CalcPsychrometrics, T_db2, altitude,
            CalculationMethod.CalcWithHumidityRatio, { W: W1 });
        }
      } else if (this.coolingMethod.value === CoolingMethod.ByState) {
        try { state2 = this.coolingTargetState.calcOutput(); } catch(e) { return null; }
        T_db2 = this.coolingTargetState.dryBulbTemp.value;
      } else if (this.coolingMethod.value === CoolingMethod.ByPower) {
        const Q = this.coolingPower.value;
        const delta_h = (Q * 1000) / massFlowDryAir;
        const h2_target = h1 - delta_h;
        const T2_sensible = (h2_target - W1 * 1061) / (0.240 + W1 * 0.444);
        const T_dp1 = incomingState.t_dp_F;
        if (T2_sensible >= T_dp1) {
          T_db2 = T2_sensible;
          const ctx = CalcContext.create();
          state2 = ctx.call(psy.CalcPsychrometrics, T_db2, altitude,
            CalculationMethod.CalcWithHumidityRatio, { W: W1 });
        } else {
          const T_lower = 32;
          let h_sat_lower;
          try { h_sat_lower = calcSatState(T_lower).h; } catch(e) { return null; }
          if (h2_target < h_sat_lower) return null;
          let T_lo = T_lower, T_hi = T_dp1;
          for (let i = 0; i < 100; i++) {
            const T_mid = (T_lo + T_hi) / 2;
            const h_mid = calcSatState(T_mid).h;
            if (Math.abs(h_mid - h2_target) < 0.0001) break;
            if (h_mid > h2_target) { T_hi = T_mid; } else { T_lo = T_mid; }
          }
          T_db2 = (T_lo + T_hi) / 2;
          state2 = calcSatState(T_db2);
        }
      } else { return null; }
      const h2 = state2.h;
      const W2 = state2.W;
      const hfg = 1061;
      const Q_total_cooling = massFlowDryAir * (h1 - h2) / 1000;
      const Q_latent_cooling = massFlowDryAir * (W1 - W2) * hfg / 1000;
      const Q_sensible_cooling = Q_total_cooling - Q_latent_cooling;
      const SHR = Q_total_cooling > 0.001 ? Q_sensible_cooling / Q_total_cooling : 1.0;
      return { state2, T_db2, Q_sensible_MBH: null, SHR, resultAirflow: null,
               Q_total_cooling, Q_latent_cooling, Q_latent_heat: null };
    }
    return null;
  }

  getResultAsChartPoint(label) {
    if (this.resultDryBulbTemp.value === null || this.resultDryBulbTemp.value === undefined) return null;
    return new psy.ChartPoint({
      name: label,
      dryBulbTemp: this.resultDryBulbTemp.value,
      wetBulbTemp: this.resultWetBulbTemp.value,
      relativeHumidity: this.resultRelativeHumidity.value,
      humidityRatio: this.resultHumidityRatio.value,
      dewpointTemp: this.resultDewpointTemp.value,
      specificVolume: this.resultSpecificVolume.value,
      enthalpy: this.resultEnthalpy.value,
    });
  }

  getMixingAirstreamChartPoint() {
    if (this.processType.value !== ProcessType.Mixing) return null;
    let res;
    try { res = this.mixingAirstream.calcOutput(); } catch(e) { return null; }
    return new psy.ChartPoint({
      name: 'Point M',
      dryBulbTemp: this.mixingAirstream.dryBulbTemp.value,
      wetBulbTemp: res.t_wb_F,
      relativeHumidity: res.RH * 100,
      humidityRatio: res.W,
      dewpointTemp: res.t_dp_F,
      specificVolume: res.v,
      enthalpy: res.h,
    });
  }
}
setupClass(PsychroProcess)

export class PsychrometricsCalculator extends InputComponent {
  init() {
    // We can't use the project watcher here, because the calculator is at
    // the global level.
    this.updater.setWatcher(Watcher.globalInstance());

    this.altitude = new Field({
      name: 'Altitude',
      type: FieldType.Length,
      watcher: this.watcher,
      min: null,
      defaultValue: 0,
    })

    this.units = Field.makeSelect('Units', ProjectUnits);
    this.prevUnits = this.units.value;
    this.units.makeUpdater((field) => {
      if (this.units.value !== this.prevUnits) {
        console.log(`Handling project units change`);
        let objectVisitor = new ObjectVisitor(this);
        objectVisitor.visit((obj, data) => {
          if (obj instanceof Field) {
            obj.onProjectUnitsChanged(field.value);
          }
        })
      }
      this.prevUnits = this.units.value;
    })

    this.points = [];
    this.processes = [];

    this.saver = new LocalStorageSaver('heatwise-psychrometrics-calculator', this);
    // Create the first point and first process by default
    this.addPoint();
    this.addProcess();

    this.serFields = [
      'altitude',
      'units',
      ser.arrayField('points', () => { return PsychrometricsPoint.create(Watcher.globalInstance(), this); }),
      ser.arrayField('processes', () => { return PsychroProcess.create(Watcher.globalInstance(), this); }),
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Psychrometrics',
      path: 'psychrometrics',
    }
  }

  addPoint() {
    const point = PsychrometricsPoint.create(Watcher.globalInstance(), this);
    // If there's an existing point, copy its state inputs to the new point
    if (this.points.length > 0) {
      const lastPoint = this.points[this.points.length - 1];
      point.dryBulbTemp.value = lastPoint.dryBulbTemp.value;
      point.airflow.value = lastPoint.airflow.value;
      point.calculationMethod.value = lastPoint.calculationMethod.value;
      point.wetBulbTemp.value = lastPoint.wetBulbTemp.value;
      point.relativeHumidity.value = lastPoint.relativeHumidity.value;
      point.humidityRatio.value = lastPoint.humidityRatio.value;
      point.dewpointTemp.value = lastPoint.dewpointTemp.value;
      point.specificVolume.value = lastPoint.specificVolume.value;
      point.enthalpy.value = lastPoint.enthalpy.value;
    }
    this.points.push(point);
    return point;
  }

  removePoint(point) {
    const index = this.points.indexOf(point);
    if (index > -1) {
      this.points.splice(index, 1);
    }
  }

  addProcess() {
    const proc = PsychroProcess.create(Watcher.globalInstance(), this);
    this.processes.push(proc);
    return proc;
  }

  removeProcess(idx) {
    if (idx >= 0 && idx < this.processes.length) {
      this.processes.splice(idx, 1);
    }
  }

  getAltitude() {
    return this.altitude.value;
  }

  getMainPoint() {
    return this.points[0];
  }

  getChartPoints() {
    const mainPoint = this.getMainPoint();
    const allPoints = [];
    try { allPoints.push(mainPoint.getAsChartPoint()); } catch(e) {}
    for (let i = 0; i < this.processes.length; i++) {
      const proc = this.processes[i];
      const mixingPoint = proc.getMixingAirstreamChartPoint();
      if (mixingPoint) allPoints.push(mixingPoint);
      const resultPt = proc.getResultAsChartPoint(`Point ${i + 2}`);
      if (resultPt) allPoints.push(resultPt);
    }
    return allPoints;
  }

  getLinePoints() {
    const mainPoint = this.getMainPoint();
    const linePoints = [];
    try { linePoints.push(mainPoint.getAsChartPoint()); } catch(e) { return linePoints; }
    for (let i = 0; i < this.processes.length; i++) {
      const resultPt = this.processes[i].getResultAsChartPoint(`Point ${i + 2}`);
      if (resultPt) linePoints.push(resultPt);
      else break;
    }
    return linePoints;
  }

  getProjectUnits() {
    return this.units.value;
  }

  async save() {
    await this.saver.save();
  }

  loadFromStorage() {
    this.saver.loadFromStorage();
  }
}
setupClass(PsychrometricsCalculator)
